import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { debounce, isArray } from "lodash";
import { ValidationTypes } from "react-validation";
import Translation from "../i18n/Translation";


/**
 * Wrapper for react-select component.
 * It will returns object or array, depending on the "multi" props value.
 * If "valueKey" props is not provided, the value for an option will be the option object itself.
 * If "labelKey" props is not provided, the label for an option will be the option converted to string.
 *
 * You'll need to define value, options and onChange props like this:
 *   <CustomSelect value={this.state.selectedItem} options={this.state.availableItems} onChange={this.onChangeItem}/>
 * Or if you want to use asynchronous option loading:
 *   <CustomSelect async value={this.state.selectedItem} loadOptions={this.loadingOptions} onChange={this.onChangeItem}/>
 */
export default class CustomSelect extends React.Component {

    static propTypes = {
        label: PropTypes.string,
        value: PropTypes.any,
        onChange: PropTypes.func.isRequired,
        multi: PropTypes.bool,
        async: PropTypes.bool,
        debounceTime: PropTypes.number,
        options: PropTypes.array,
        loadOptions: PropTypes.func,
        noResultsText: PropTypes.string,
        autoload: PropTypes.bool
    };

    static defaultProps = {
        options: [],
        validationType: ValidationTypes.REACT_SELECT, // Props required by Validation system
        noResultsText: Translation.translate("react-select.no-result-found"),
        searchPromptText: Translation.translate("search.starttyping"),
        searchingText: Translation.translate("search.loading"),
        autoload: false
    };

    constructor(props) {
        super(props);

        this.debounce = debounce(this._callLoadOption, this.props.debounceTime);
    }

    getOptions = () => {
        if (!this.props.labelKey)
            return this.props.options.map((option) => ({value: option, label: option.toString()}));
        else
            return this.props.options;
    };

    onChange = (selectedItems) => (this.props.multi ? this._onChangeMulti(selectedItems) : this._onChangeSingle(selectedItems));

    _onChangeSingle = (selectedItem) => {
        // Tests on array prevent a bug: when pushing backspace key to delete the selected value, it returns a empty array instead of null
        if (selectedItem === null || (isArray(selectedItem) && selectedItem.length === 0))
            return this.props.onChange(null);

        return this.props.labelKey ? this.props.onChange(selectedItem) : this.props.onChange(selectedItem.value);
    };
    _onChangeMulti = (selectedItems) => {
        if (selectedItems === null)
            return this.props.onChange([]);

        this.props.labelKey ? this.props.onChange(selectedItems) : this.props.onChange(selectedItems.map((selectedItem) => (selectedItem.value)));
    };

    loadOptions = (input, callback) => {
        if (input && input.length !== 0) {
            if (this.props.debounceTime)
                this.debounce(input, callback);
            else
                this._callLoadOption(input, callback);
        } else {
            callback(); // Call callback() to avoid infinite spinner
        }
    };

    _callLoadOption = (input, callback) => {
        this.props.loadOptions(input, (data) => {
            callback(null, {complete: true, options: data});
        });
    };


    renderLabel = () => (
        <label className="control-label">
            <span>{ this.props.label }</span>
        </label>
    );

    render = () => {
        let select;

        if (this.props.async)
            select = <Select.Async {...this.props} onChange={this.onChange} loadOptions={this.loadOptions} filterOptions={false}/>;
        else
            select = <Select {...this.props} onChange={this.onChange} options={this.getOptions()}/>;

        return (
            <div className="form-group">
                { this.props.label ? this.renderLabel() : null }
                {select}
            </div>
        );
    };
}



// WEBPACK FOOTER //
// ./js/mywort/commons/components/CustomSelect.jsx